import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/admin/ui/card';
import { Badge } from '@/components/admin/ui/badge';
import { format } from 'date-fns';
import { Clock, User, Bot, Users } from 'lucide-react';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { STAGE_LABELS } from '@/lib/booking-lifecycle';
import type { LifecycleStage } from '@/lib/booking-lifecycle';

interface TimelineEvent {
  id: string;
  to_stage: LifecycleStage;
  changed_by: string | null;
  changed_at: string | null;
  reason: string | null;
  changed_by_user?: {
    full_name?: string;
    role?: string;
  } | null;
}

interface BookingTimelineProps {
  bookingId: string;
}

export const BookingTimeline = ({ bookingId }: BookingTimelineProps) => {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTimelineEvents();
  }, [bookingId]);

  const fetchTimelineEvents = async () => {
    try {
      setLoading(true);
      const { data: historyData, error } = await supabaseAdmin
        .from('booking_stage_history')
        .select('*')
        .eq('booking_id', bookingId)
        .order('changed_at', { ascending: true });

      if (error) throw error;

      // Fetch user data for events that have changed_by
      const userIds = [...new Set(historyData?.filter(h => h.changed_by).map(h => h.changed_by) || [])];
      let usersMap: Record<string, any> = {};

      if (userIds.length > 0) {
        const { data: usersData } = await supabaseAdmin
          .from('users')
          .select('id, full_name, role')
          .in('id', userIds);

        if (usersData) {
          usersMap = Object.fromEntries(usersData.map(u => [u.id, u]));
        }
      }

      // Combine history with user data
      const eventsWithUsers = historyData?.map(event => ({
        ...event,
        changed_by_user: event.changed_by ? usersMap[event.changed_by] : null,
      })) || [];

      setEvents(eventsWithUsers as any);
    } catch (error) {
      console.error('Error fetching timeline events:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEventSource = (event: TimelineEvent) => {
    // If no user ID, it's a system event
    if (!event.changed_by) {
      return {
        label: 'System',
        icon: Bot,
        color: 'text-gray-500',
        bgColor: 'bg-gray-100 dark:bg-gray-800',
      };
    }

    // Check role to determine source
    const role = event.changed_by_user?.role?.toLowerCase() || '';

    if (role.includes('admin') || role === 'admin') {
      return {
        label: 'Admin',
        icon: User,
        color: 'text-purple-600',
        bgColor: 'bg-purple-100 dark:bg-purple-900/30',
      };
    }

    if (role.includes('customer') || role === 'customer') {
      return {
        label: 'Client',
        icon: User,
        color: 'text-blue-600',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
      };
    }

    if (role.includes('photographer') || role === 'photographer' || role === 'videographer') {
      return {
        label: 'Talent',
        icon: Users,
        color: 'text-green-600',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
      };
    }

    // Default to user
    return {
      label: event.changed_by_user?.full_name || 'User',
      icon: User,
      color: 'text-gray-600',
      bgColor: 'bg-gray-100 dark:bg-gray-800',
    };
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-6">
          <div className="text-center text-sm text-muted-foreground">Loading timeline...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Clock className="h-5 w-5 text-primary" />
          Timeline / Checklist
        </CardTitle>
        <CardDescription>
          Complete booking lifecycle history
        </CardDescription>
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <div className="text-center py-4 text-sm text-muted-foreground">
            No timeline events yet
          </div>
        ) : (
          <div className="space-y-0">
            {events.map((event, index) => {
              const source = getEventSource(event);
              const Icon = source.icon;
              const isLast = index === events.length - 1;

              return (
                <div key={event.id} className="relative">
                  {/* Timeline line */}
                  {!isLast && (
                    <div className="absolute left-5 top-10 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" />
                  )}

                  {/* Event row */}
                  <div className="flex items-start gap-4 pb-6">
                    {/* Icon */}
                    <div className={`relative z-10 flex items-center justify-center h-10 w-10 rounded-full ${source.bgColor} flex-shrink-0`}>
                      <Icon className={`h-5 w-5 ${source.color}`} />
                    </div>

                    {/* Event details */}
                    <div className="flex-1 pt-1">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <h4 className="font-medium text-sm text-foreground">
                            {STAGE_LABELS[event.to_stage] || event.to_stage}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {source.label}
                            </Badge>
                            {event.reason && (
                              <Badge variant="outline" className="text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400">
                                Manual Override
                              </Badge>
                            )}
                          </div>
                        </div>
                        <time className="text-xs text-muted-foreground whitespace-nowrap">
                          {event.changed_at ? format(new Date(event.changed_at), 'MMM d, yyyy h:mm a') : 'N/A'}
                        </time>
                      </div>

                      {/* Override reason if present */}
                      {event.reason && (
                        <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded text-xs text-amber-800 dark:text-amber-300">
                          <strong>Reason:</strong> {event.reason}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
